import React, { useEffect, useMemo, useRef, useState } from 'react';

import { isEqual } from 'lodash';
import classNames from 'classnames';

import GoogleMapStyles from './google-map-design';
import { DEFAULT_MAX_ZOOM, DEFAULT_MIN_ZOOM, setMapCenter, setMapZoom } from './utils/utils';
import { useBoundsHandler, useButtonsLayer, useGoogleMarkers, useGooglePolylines, useTrafficLayer } from './hooks';
import { MapOptionsProps } from '../types';
import usePrevious from '../use-previous';
import { BRINGG_MAP_ID } from '../bringg-map';
import { getMapViewPreference, setMapViewPreference } from './utils/local-storage-utils';

import styles from './bringg-google-map-provider.module.scss';

const BringgGoogleMapProvider = ({
	markers,
	mapCenter,
	mapZoom,
	maxZoom,
	minZoom,
	polylines,
	mapElementClassName,
	fullscreenControl,
	allowTrafficLayer,
	translations,
	buttons,
	isPlaying,
	forceRecenterOnAddingMarkers,
	forceRecenterOnUpdatingMarkers,
	selectedMarkers,
	onSelectMarker,
	clearSelectedMarkers,
	onMapClick,
	boundsPadding,
	options = {},
	clusterOptions,
	localStorageKey
}: MapOptionsProps) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const unsubscribeFromEventRef = useRef<google.maps.MapsEventListener[]>([]);
	const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
	const [map, setMap] = useState<google.maps.Map | null>(null);
	const [shouldAutoFocusMap, setShouldAutoFocusMap] = useState<boolean>(true);
	const prevMarkers = usePrevious(markers);
	const prevMarkersLength = usePrevious(markers?.length);

	const markersList = useMemo(() => {
		return markers || [];
	}, [markers]);

	useEffect(() => {
		let newMap: google.maps.Map;
		if (mapRef.current) {
			const mapViewPreference = localStorageKey ? getMapViewPreference(localStorageKey) : null;
			const initialMapTypeId =
				mapViewPreference === false ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.ROADMAP; // Default to map view if null or true

			newMap = new google.maps.Map(mapRef.current, {
				center: mapCenter,
				styles: GoogleMapStyles,
				zoom: mapZoom,
				maxZoom: maxZoom || DEFAULT_MAX_ZOOM,
				minZoom: minZoom || DEFAULT_MIN_ZOOM,
				fullscreenControl,
				controlSize: 24,
				mapTypeControlOptions: {
					position: google.maps.ControlPosition.RIGHT_BOTTOM
				},
				mapTypeId: initialMapTypeId,
				mapId: BRINGG_MAP_ID,
				streetViewControlOptions: {
					position: google.maps.ControlPosition.RIGHT_CENTER
				},
				...options
			});

			unsubscribeFromEventRef.current.push(
				newMap.addListener('dragstart', () => {
					setShouldAutoFocusMap(false);
				})
			);

			unsubscribeFromEventRef.current.push(
				newMap.addListener('bounds_changed', () => {
					// @ts-ignore
					if (newMap?.systemZoomChange) {
						// @ts-ignore
						newMap.systemZoomChange = false;
					} else {
						setShouldAutoFocusMap(false);
					}
				})
			);

			if (localStorageKey) {
				unsubscribeFromEventRef.current.push(
					newMap.addListener('maptypeid_changed', () => {
						const currentMapTypeId = newMap.getMapTypeId();
						const isMapView = currentMapTypeId === google.maps.MapTypeId.ROADMAP;
						setMapViewPreference(localStorageKey, isMapView);
					})
				);
			}

			setMap(newMap);
		}

		return () => {
			google.maps.event.clearInstanceListeners(newMap);
		};
	}, []);

	useEffect(() => {
		if (!map) return;

		if (clickListenerRef.current) {
			google.maps.event.removeListener(clickListenerRef.current);
			const index = unsubscribeFromEventRef.current.indexOf(clickListenerRef.current);
			if (index > -1) {
				unsubscribeFromEventRef.current.splice(index, 1);
			}
			clickListenerRef.current = null;
		}

		clickListenerRef.current = google.maps.event.addListener(map, 'click', (e: google.maps.MapMouseEvent) => {
			clearSelectedMarkers?.();
			onMapClick?.(e);
		});

		unsubscribeFromEventRef.current.push(clickListenerRef.current);
	}, [map, onMapClick, clearSelectedMarkers]);

	useEffect(() => {
		if (!map || !mapCenter) return;
		setMapCenter(map, mapCenter);
		mapZoom && setMapZoom(map, mapZoom);
	}, [map, mapCenter]);

	useEffect(() => {
		if (!map) return;
		mapZoom && setMapZoom(map, mapZoom);
	}, [map, mapZoom]);

	useEffect(() => {
		if (isPlaying && !shouldAutoFocusMap) {
			setShouldAutoFocusMap(true);
		}
	}, [isPlaying]);

	const shouldPanMap =
		(forceRecenterOnAddingMarkers && prevMarkersLength !== markers?.length) ||
		(forceRecenterOnUpdatingMarkers && !isEqual(prevMarkers, markers));

	useGoogleMarkers({
		markers: markersList,
		map,
		shouldAutoFocusMap,
		onSelectMarker,
		selectedMarkers,
		forceRecenterOnAddingMarkers,
		clusterOptions
	});

	useEffect(() => {
		return () => {
			unsubscribeFromEventRef.current.forEach(listener => {
				listener.remove();
			});
			unsubscribeFromEventRef.current = [];
		};
	}, []);

	useGooglePolylines(map, polylines);
	useBoundsHandler(map, markers, polylines, shouldAutoFocusMap || shouldPanMap, boundsPadding);

	useTrafficLayer({
		trafficButtonTranslation: translations?.traffic,
		allowTrafficLayer: !!allowTrafficLayer,
		map,
		localStorageKey
	});
	useButtonsLayer({ map, buttons: buttons || [] });

	return (
		<div
			ref={mapRef}
			data-test-id={'bringg-google-map-provider'}
			className={classNames(styles.bringgGoogleMapContainer, mapElementClassName)}
		/>
	);
};

export default BringgGoogleMapProvider;
